/**
 * Base URL for Supabase Edge Functions (`{base}/functions/v1/{name}`).
 *
 * Defaults to `VITE_SUPABASE_URL`. Set `VITE_SUPABASE_FUNCTIONS_URL` to your hosted
 * project (e.g. `https://<ref>.supabase.co`) when `VITE_SUPABASE_URL` is the local
 * CLI URL (`http://127.0.0.1:54321`) but functions are only deployed remotely.
 * Use the anon key for that same hosted project in `VITE_SUPABASE_ANON_KEY`.
 */
export function getSupabaseFunctionsBaseUrl(): string | null {
  const explicit = (import.meta.env.VITE_SUPABASE_FUNCTIONS_URL as string | undefined)?.trim();
  const fallback = (import.meta.env.VITE_SUPABASE_URL as string | undefined)?.trim();
  const raw = explicit || fallback;
  if (!raw) return null;
  return raw.replace(/\/$/, "");
}

export function getSupabaseEdgeFunctionUrl(functionName: string): string | null {
  const base = getSupabaseFunctionsBaseUrl();
  if (!base) return null;
  const name = functionName.replace(/^\//, "").replace(/\/$/, "");
  return `${base}/functions/v1/${name}`;
}

function parseUrlHost(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  try {
    const u = new URL(trimmed.includes("://") ? trimmed : `https://${trimmed}`);
    return u.host;
  } catch {
    return null;
  }
}

let warnedDatabaseFunctionsHostMismatch = false;

/**
 * In dev, warn when the REST URL and Edge Functions base resolve to different hosts.
 * Split config is a common cause of “invite pending in UI but set-password token not found”
 * (invite row in one Postgres, accept-org-admin-invite reading another).
 */
export function warnIfSupabaseDatabaseAndFunctionsHostsMismatch(): void {
  if (warnedDatabaseFunctionsHostMismatch) return;
  if (typeof import.meta.env === "undefined" || !import.meta.env.DEV) return;

  const dbRaw = (import.meta.env.VITE_SUPABASE_URL as string | undefined)?.trim() ?? "";
  const dbHost = parseUrlHost(dbRaw);
  const fnRaw = getSupabaseFunctionsBaseUrl() ?? "";
  const fnHost = fnRaw ? parseUrlHost(fnRaw) : null;

  if (!dbHost || !fnHost || dbHost === fnHost) return;

  warnedDatabaseFunctionsHostMismatch = true;
  console.warn(
    `[fit2lift] VITE_SUPABASE_URL host (${dbHost}) and Edge Functions host (${fnHost}) differ. ` +
      "The admin UI reads/writes the database for the REST URL; Edge Functions use their project's database. " +
      "If those are not the same Postgres, portal Contact invite links will always fail at set-password. " +
      "Fix: use one Supabase project for both URLs, or set VITE_SUPABASE_FUNCTIONS_URL to match VITE_SUPABASE_URL's project (or leave it unset).",
  );
}
